import Vue from 'vue';

/**
 * Live timer tracking for admin real-time view.
 *
 * Flow:
 *   Every PATCH /api/task/loggers/{id} → fires LoggerUpdated event → listener
 *   publishes logger payload to MQTT → this store receives it → UI updates.
 *
 * Pure event-driven: no HTTP pull. The state is empty until events arrive.
 * When a logger is removed from the DB (delete), a payload with is_running=false
 * and time_spend=final may be emitted last — the admin still sees the paused
 * row. A subsequent timer_deleted event (if wired later) would drop the row.
 */
export default {
    namespaced: true,

    state: {
        // key = `${task_user_id}:${task_id}` -> session object
        sessions: {},
        mqttConnected: false,
    },

    getters: {
        allSessions: state => Object.values(state.sessions),

        // Everything we track (running + paused)
        activeSessions: state => Object.values(state.sessions),

        // Badge view: running only, grouped by project
        sessionsByProject: state => {
            const map = {};
            for (const s of Object.values(state.sessions)) {
                if (!s.is_running) continue;
                if (!s.project_id) continue;
                if (!map[s.project_id]) map[s.project_id] = [];
                map[s.project_id].push(s);
            }
            return map;
        },

        liveCountForProject: (_state, getters) => projectId =>
            (getters.sessionsByProject[projectId] || []).length,

        liveUsersForProject: (_state, getters) => projectId =>
            getters.sessionsByProject[projectId] || [],

        mqttConnected: state => state.mqttConnected,
    },

    mutations: {
        UPSERT_SESSION(state, payload) {
            // Key by log_id — each logger row has its own key even if the
            // same user runs multiple timers on the same task.
            const key = String(payload.log_id || `${payload.task_user_id}:${payload.task_id}`);
            const existing = state.sessions[key];

            // Resync the clock on every event so the dock stays aligned with
            // the user's logger heartbeat. Between events, the UI extrapolates
            // from `time_spend_at_event + (now - received_at) / 1000`.
            const timeSpendSec = Number(payload.time_spend || 0);

            Vue.set(state.sessions, key, {
                ...existing,
                ...payload,
                key,
                time_spend_at_event: timeSpendSec,
                received_at: Date.now(),
                is_running: !!payload.is_running,
            });
        },

        REMOVE_SESSION(state, payload) {
            const key = String(payload.log_id || `${payload.task_user_id}:${payload.task_id}`);
            Vue.delete(state.sessions, key);
        },

        CLEAR_SESSIONS(state) {
            state.sessions = {};
        },

        SET_MQTT_CONNECTED(state, value) {
            state.mqttConnected = !!value;
        },
    },

    actions: {
        /**
         * Handle an MQTT payload published by the Laravel listener.
         * Payload shape (from PublishLoggerUpdate::handle):
         *   { log_id, task_id, task_user_id, project_id, project_name,
         *     task_title, user_name, user_email, color,
         *     time_spend, is_running, description, date, start_time }
         */
        handleMqttEvent({ commit }, payload) {
            if (!payload || !payload.task_id || !payload.task_user_id) return;

            if (payload.event === 'timer_deleted' || payload.has_been_close) {
                commit('REMOVE_SESSION', payload);
                return;
            }

            commit('UPSERT_SESSION', payload);
        },

        reset({ commit }) {
            commit('CLEAR_SESSIONS');
            commit('SET_MQTT_CONNECTED', false);
        },
    },
};
